/**
 * Pure compute helpers for VitalsData.
 *
 * Extracted from VitalsSection so that the component file only exports
 * React components (required by react-refresh / Fast Refresh).
 */

import { getXpForNextLevel } from '@/lib/utils';
import { effectiveMaxHp } from '@/lib/character-status';
import type { Character } from '@/lib/stores/lobbyStore.types';
import type { GameCharacterInfo } from '@danda/shared';

// ─── VitalsData ───────────────────────────────────────────────

export interface VitalsData {
  hpCurrent: number;
  hpMax: number;
  xpCurrent: number;
  xpNext: number;
  xpPercent: number;
  canLevelUp: boolean;
  level: number;
  ac: number;
  dex: number;
  initiativeBonus: number;
  speed: number;
  str: number;
  con: number;
  int: number;
  wis: number;
  cha: number;
  proficiencyBonus: number;
  hitDie: string;
  passivePerception: number;
  darkvision?: number;
  spellcasting?: { spell_save_dc: number; spell_attack_bonus: number };
  tempHp: number;
  /** Whether the character is concentrating (set for all observers). */
  isConcentrating: boolean;
  /**
   * Spell name when the observer is permitted to see it. `null` means
   * "concentrating but anonymized" (hostile/neutral observer). `undefined`
   * means not concentrating.
   */
  concentrationSpell?: string | null;
  conditions: Array<string | { name: string; [key: string]: unknown }>;
  deathSaveSuccesses?: number;
  deathSaveFailures?: number;
  alignment?: string;
}

// ─── Pure compute helpers ─────────────────────────────────────

/** Compute VitalsData from lobby selectedCharacter */
export function computeLobbyVitals(selectedCharacter: Character | null): VitalsData | null {
  if (!selectedCharacter) return null;
  const permanent = selectedCharacter.permanent as Record<string, unknown> | undefined;
  const session = selectedCharacter.session as Record<string, unknown> | undefined;
  if (!permanent) return null;

  // Effective max HP must match the backend `CharacterData::max_hp()`:
  // permanent.max_hp + hp_max_bonus (e.g. Aid) - hp_max_reduction (e.g. Life
  // Drain). Using the raw permanent value made current HP (which carries the
  // Aid bonus) exceed the shown max, e.g. 37/32 after an Aid buff.
  const hpMax = effectiveMaxHp(
    permanent as { max_hp?: number },
    session as { hp_max_bonus?: number; hp_max_reduction?: number } | undefined
  );
  const hpCurrent = (session?.hp as number) ?? hpMax;
  const level = (permanent.level as number) ?? 1;
  const xpCurrent = (permanent.xp as number) ?? 0;
  const xpNext = getXpForNextLevel(level);
  const xpPercent = (xpCurrent / xpNext) * 100;

  const abilities = (permanent.abilities as Record<string, number>) ?? {};
  const str = abilities.str ?? 10;
  const dex = abilities.dex ?? 10;
  const con = abilities.con ?? 10;
  const int = abilities.int ?? 10;
  const wis = abilities.wis ?? 10;
  const cha = abilities.cha ?? 10;

  const speedValue = permanent.speed as number | Record<string, number> | undefined;
  const speed =
    typeof speedValue === 'number'
      ? speedValue
      : (speedValue?.walk ??
        speedValue?.fly ??
        speedValue?.swim ??
        speedValue?.climb ??
        speedValue?.burrow ??
        30);

  const proficiencyBonus =
    (permanent.proficiency_bonus as number) ?? Math.floor((level - 1) / 4) + 2;

  const dexMod = Math.floor((dex - 10) / 2);
  const computed = permanent.computed as { initiative?: number } | undefined;
  const initiativeBonus = computed?.initiative ?? dexMod;

  const senses = permanent.senses as Record<string, unknown> | undefined;
  const passivePerception =
    (senses?.passive_perception as number) ?? 10 + Math.floor((wis - 10) / 2);

  const spellcasting = permanent.spellcasting as
    | { spell_save_dc: number; spell_attack_bonus: number }
    | undefined;

  const concentration = session?.concentration as string | { spell: string } | undefined;
  const concentrationStr =
    concentration == null
      ? undefined
      : typeof concentration === 'string'
        ? concentration
        : concentration.spell;

  return {
    hpCurrent,
    hpMax,
    xpCurrent,
    xpNext,
    xpPercent,
    canLevelUp: xpCurrent >= xpNext,
    level,
    ac: (permanent.ac as number) ?? 10,
    dex,
    initiativeBonus,
    speed,
    str,
    con,
    int,
    wis,
    cha,
    proficiencyBonus,
    hitDie: (permanent.hit_die as string) ?? 'd8',
    passivePerception,
    darkvision: senses?.darkvision as number | undefined,
    spellcasting: spellcasting?.spell_save_dc != null ? spellcasting : undefined,
    tempHp: (session?.temp_hp as number) ?? 0,
    isConcentrating: concentrationStr != null,
    concentrationSpell: concentrationStr,
    conditions: (session?.conditions as VitalsData['conditions']) ?? [],
    alignment: typeof permanent.alignment === 'string' ? permanent.alignment : undefined,
  };
}

/** Compute VitalsData from gameStore partyMember.
 *  Top-level fields (abilities, speed, etc.) may be populated by incremental
 *  state changes. On initial game_state connect they live inside the
 *  permanent/session JSON blobs, so we fall back to those.
 */
export function computeGameVitals(gameCharacter: GameCharacterInfo | null): VitalsData | null {
  if (!gameCharacter) return null;

  const perm = gameCharacter.permanent;
  const sess = gameCharacter.session;

  const maxHp = gameCharacter.maxHp ?? (perm?.max_hp as number) ?? 0;
  const hp = gameCharacter.hp ?? (sess?.hp as number) ?? maxHp;
  const level = gameCharacter.level ?? (perm?.level as number) ?? 1;
  const xpCurrent = gameCharacter.xp ?? (perm?.xp as number) ?? 0;
  const xpNext = getXpForNextLevel(level);
  const xpPercent = (xpCurrent / xpNext) * 100;

  const abilities = gameCharacter.abilities ?? (perm?.abilities as GameCharacterInfo['abilities']);
  const wis = abilities?.wis ?? 10;
  const dex = abilities?.dex ?? 10;

  const senses = gameCharacter.senses ?? (perm?.senses as GameCharacterInfo['senses']);
  const passivePerception = senses?.passive_perception ?? 10 + Math.floor((wis - 10) / 2);

  const spellcasting =
    gameCharacter.spellcasting ?? (perm?.spellcasting as GameCharacterInfo['spellcasting']);

  const speedValue = gameCharacter.speed ?? perm?.speed;
  const speed =
    typeof speedValue === 'number'
      ? speedValue
      : typeof speedValue === 'object' && speedValue != null
        ? ((speedValue as Record<string, number>).walk ?? 30)
        : 30;

  const dexMod = Math.floor((dex - 10) / 2);
  const computed = perm?.computed as { initiative?: number } | undefined;
  const initiativeBonus = computed?.initiative ?? dexMod;

  return {
    hpCurrent: hp,
    hpMax: maxHp,
    xpCurrent,
    xpNext,
    xpPercent,
    canLevelUp: xpCurrent >= xpNext,
    level,
    ac: gameCharacter.ac ?? (perm?.ac as number) ?? 10,
    dex,
    initiativeBonus,
    speed,
    str: abilities?.str ?? 10,
    con: abilities?.con ?? 10,
    int: abilities?.int ?? 10,
    wis,
    cha: abilities?.cha ?? 10,
    proficiencyBonus:
      gameCharacter.proficiencyBonus ??
      (perm?.proficiency_bonus as number) ??
      Math.floor((level - 1) / 4) + 2,
    hitDie: gameCharacter.hitDie ?? (perm?.hit_die as string) ?? 'd8',
    passivePerception,
    darkvision: senses?.darkvision,
    spellcasting:
      spellcasting?.spell_save_dc != null
        ? {
            spell_save_dc: spellcasting.spell_save_dc,
            spell_attack_bonus: spellcasting.spell_attack_bonus ?? 0,
          }
        : undefined,
    tempHp: gameCharacter.tempHp ?? (sess?.temp_hp as number) ?? 0,
    isConcentrating: gameCharacter.isConcentrating ?? gameCharacter.concentrationSpell != null,
    concentrationSpell: gameCharacter.concentrationSpell ?? null,
    conditions: (gameCharacter.conditions ?? []) as VitalsData['conditions'],
    deathSaveSuccesses: gameCharacter.deathSaveSuccesses,
    deathSaveFailures: gameCharacter.deathSaveFailures,
    alignment: typeof perm?.alignment === 'string' ? perm.alignment : undefined,
  };
}
